import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Ticket, Users, Mail, Trophy, Shield, ArrowLeft, ChevronLeft, ChevronRight, Circle, Smartphone, X, ZoomIn, BarChart3, Bell, CloudSun } from 'lucide-react';

const slides = [
  {
    mobile: true,
    images: ["/screenshots/14_weather_forecast.jpg", "/screenshots/16_weather_calendar.jpg"],
    title: "Built-In Weather Forecasts",
    desc: "14-day forecast with real weather icons, hi/lo temps, and rain chances right on your calendar. Tap the weather button for a full forecast or switch to live radar — plan around the weather without leaving the app.",
    icon: CloudSun,
    accent: "from-sky-500 to-cyan-500"
  },
  {
    mobile: true,
    images: ["/screenshots/08_mobile_events.jpg", "/screenshots/09_mobile_calendar.jpg"],
    title: "Beautiful On Every Device",
    desc: "Full-featured mobile experience — browse events, check your calendar, and manage tickets right from your phone. No app download needed.",
    icon: Smartphone,
    accent: "from-cyan-500 to-blue-500"
  },
  {
    mobile: true,
    images: ["/screenshots/07_mobile_login.jpg", "/screenshots/10_mobile_tickets.jpg"],
    title: "Your Pocket Ticket Manager",
    desc: "Quick biometric login, compact event views, and full ticket details — optimized for one-handed use on the go.",
    icon: Smartphone,
    accent: "from-indigo-500 to-violet-500"
  },
  {
    image: "/screenshots/02_events.jpg",
    title: "Your Command Center",
    desc: "Every team, every season, every event — managed from one clean dashboard. NBA, NHL, NFL, MLB, concerts, and family events all in one place.",
    icon: Trophy,
    accent: "from-blue-500 to-cyan-500"
  },
  {
    image: "/screenshots/03_calendar.jpg",
    title: "Unified Calendar View",
    desc: "See your entire schedule at a glance. Color-coded by team, with ticket assignments, parking passes, and live scores — all on one calendar.",
    icon: Calendar,
    accent: "from-emerald-500 to-teal-500"
  },
  {
    image: "/screenshots/04_tickets.jpg",
    title: "Complete Ticket Tracking",
    desc: "Track every ticket and parking pass across the season. See dollar values, win/loss records, and who's assigned to every game.",
    icon: Ticket,
    accent: "from-amber-500 to-orange-500"
  },
  {
    image: "/screenshots/11_friends_family.jpg",
    title: "Friends & Family Sharing",
    desc: "Assign tickets to friends and family for any event. Add notify contacts so everyone involved gets automatic daily and weekly email reminders — no one misses a game or gathering.",
    icon: Users,
    accent: "from-pink-500 to-rose-500"
  },
  {
    image: "/screenshots/06_email.jpg",
    title: "Smart Email Reminders",
    desc: "Automated daily and weekly email reminders keep everyone in the loop — partners, friends, and family all get personalized schedules. Preview exactly what each person will receive before sending.",
    icon: Bell,
    accent: "from-violet-500 to-purple-500"
  },
  {
    image: "/screenshots/12_report_summary.jpg",
    title: "End-of-Season Reports",
    desc: "Generate detailed season reports showing each partner's ticket usage, contributions, and who owes what. Print, email to all partners, or settle up — no more spreadsheet headaches.",
    icon: BarChart3,
    accent: "from-teal-500 to-emerald-500"
  },
  {
    image: "/screenshots/13_settlement.jpg",
    title: "Partner Settlement",
    desc: "Crystal-clear financial settlement at season's end. See initial contributions vs. actual usage, who's over or under, and the exact dollar amount to square up between partners.",
    icon: BarChart3,
    accent: "from-green-500 to-lime-500"
  },
  {
    image: "/screenshots/05_admin.jpg",
    title: "Full Admin Control",
    desc: "Manage users, set permissions, configure email automation, and run reports. Complete control over your ticket empire.",
    icon: Shield,
    accent: "from-rose-500 to-pink-500"
  }
];

const features = [
  { icon: Trophy, title: "Multi-Sport", desc: "NBA, NHL, NFL, MLB, concerts, family events" },
  { icon: Calendar, title: "Unified Calendar", desc: "Every game and event on one view" },
  { icon: Ticket, title: "Ticket Tracking", desc: "Track assignments, parking, and dollar values" },
  { icon: CloudSun, title: "Weather Overlay", desc: "14-day forecast & live radar on your calendar" },
  { icon: Users, title: "Friends & Family", desc: "Assign tickets and auto-notify everyone" },
  { icon: Bell, title: "Daily & Weekly Emails", desc: "Automated reminders for all partners & guests" },
  { icon: BarChart3, title: "Season Reports", desc: "Settlement reports to square up partners" },
];

export default function LearnMore() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState(1);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // Build flat list of all images for lightbox navigation
  const allImages = slides.reduce((acc, s, slideIdx) => {
    if (s.mobile) {
      s.images.forEach((img, i) => acc.push({ src: img, title: s.title, desc: s.desc, slideIdx }));
    } else {
      acc.push({ src: s.image, title: s.title, desc: s.desc, slideIdx });
    }
    return acc;
  }, []);

  const openLightbox = (imgSrc) => {
    const idx = allImages.findIndex(i => i.src === imgSrc);
    setLightboxIdx(idx >= 0 ? idx : 0);
  };

  const lightboxPrev = () => setLightboxIdx(i => (i - 1 + allImages.length) % allImages.length);
  const lightboxNext = () => setLightboxIdx(i => (i + 1) % allImages.length);

  const goTo = useCallback((idx) => {
    setDirection(idx > current ? 1 : -1);
    setCurrent(idx);
    setIsAutoPlaying(false);
  }, [current]);

  const next = useCallback(() => {
    setDirection(1);
    setCurrent(c => (c + 1) % slides.length);
  }, []);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent(c => (c - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, next]);

  const slide = slides[current];
  const Icon = slide.icon;

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white" data-testid="learn-more-page">
      {/* Nav */}
      <nav className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <button
          data-testid="learn-more-back-btn"
          onClick={() => navigate('/')}
          className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="text-lg font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
          TicketASaurus
        </div>
        <button
          data-testid="learn-more-get-started-btn"
          onClick={() => navigate('/?register=true')}
          className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors"
        >
          Get Started
        </button>
      </nav>

      {/* Hero */}
      <div className="text-center px-4 pt-6 pb-10">
        <h1 className="text-4xl sm:text-5xl font-bold mb-3 bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-400 bg-clip-text text-transparent">
          Tame Your Tickets
        </h1>
        <p className="text-base text-gray-400 max-w-xl mx-auto">
          Season tickets across multiple teams? Family events? Concerts? Manage it all from one powerful dashboard.
        </p>
      </div>

      {/* Screenshot Showcase */}
      <div className="max-w-6xl mx-auto px-4 pb-12">
        <div className="relative rounded-2xl overflow-hidden bg-gray-900/50 border border-gray-700/40 shadow-2xl">
          {/* Main content area */}
          <div className="flex flex-col lg:flex-row">
            {/* Screenshot */}
            <div className="lg:w-2/3 relative bg-black/30">
              <div className="aspect-[16/10] relative overflow-hidden">
                {slide.mobile ? (
                  /* Phone mockup layout - two phones side by side */
                  <div
                    key={current}
                    className="w-full h-full flex items-center justify-center gap-6 px-8 py-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
                    style={{ animation: 'fadeSlide 0.4s ease-out' }}
                  >
                    {slide.images.map((img, i) => (
                      <div key={i} className="relative cursor-pointer group/phone" onClick={() => openLightbox(img)} style={{ height: '92%' }}>
                        {/* Phone frame */}
                        <div className="h-full aspect-[9/19.5] bg-gray-950 rounded-[2rem] border-[3px] border-gray-600 overflow-hidden shadow-2xl shadow-blue-500/10 relative group-hover/phone:border-blue-500/60 transition-colors">
                          {/* Notch */}
                          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-gray-950 rounded-b-2xl z-10" />
                          {/* Screen */}
                          <img
                            src={img}
                            alt={`${slide.title} ${i + 1}`}
                            className="w-full h-full object-cover object-top"
                          />
                          {/* Zoom hint */}
                          <div className="absolute inset-0 bg-black/0 group-hover/phone:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover/phone:opacity-100">
                            <ZoomIn size={28} className="text-white drop-shadow-lg" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    key={current}
                    className="w-full h-full cursor-pointer group/img"
                    onClick={() => openLightbox(slide.image)}
                    style={{ animation: 'fadeSlide 0.4s ease-out' }}
                  >
                    <img
                      data-testid={`slide-image-${current}`}
                      src={slide.image}
                      alt={slide.title}
                      className="w-full h-full object-cover object-top"
                    />
                    {/* Zoom hint overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover/img:opacity-100">
                      <div className="bg-black/60 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2">
                        <ZoomIn size={18} className="text-white" />
                        <span className="text-white text-sm font-medium">Click to expand</span>
                      </div>
                    </div>
                  </div>
                )}
                {/* Gradient overlay on right edge for text blend */}
                <div className="hidden lg:block absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-gray-900/90 to-transparent" />
              </div>

              {/* Nav arrows */}
              <button
                data-testid="slide-prev-btn"
                onClick={prev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors backdrop-blur-sm"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                data-testid="slide-next-btn"
                onClick={() => { next(); setIsAutoPlaying(false); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors backdrop-blur-sm"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Feature description panel */}
            <div className="lg:w-1/3 p-6 lg:p-8 flex flex-col justify-center bg-gradient-to-b lg:bg-gradient-to-r from-gray-900/60 to-gray-900/90">
              <div
                key={current}
                style={{ animation: 'fadeUp 0.4s ease-out' }}
              >
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${slide.accent} bg-opacity-20 mb-4`}
                  style={{ background: `linear-gradient(135deg, rgba(59,130,246,0.15), rgba(6,182,212,0.15))` }}
                >
                  <Icon size={16} className="text-blue-400" />
                  <span className="text-xs font-semibold text-blue-300 uppercase tracking-wider">
                    {current + 1} of {slides.length}
                  </span>
                </div>
                <h2 className="text-2xl font-bold mb-3 text-white">{slide.title}</h2>
                <p className="text-gray-400 leading-relaxed text-sm">{slide.desc}</p>
              </div>
            </div>
          </div>

          {/* Dots navigation */}
          <div className="flex items-center justify-center gap-2 py-3 bg-gray-900/80 border-t border-gray-800/50">
            {slides.map((s, i) => (
              <button
                key={i}
                data-testid={`slide-dot-${i}`}
                onClick={() => goTo(i)}
                className="group flex items-center gap-1.5 px-2 py-1 rounded-md transition-all hover:bg-gray-800/50"
              >
                <Circle
                  size={8}
                  className={`transition-all ${i === current ? 'fill-blue-400 text-blue-400' : 'fill-gray-600 text-gray-600 group-hover:fill-gray-400 group-hover:text-gray-400'}`}
                />
                <span className={`text-xs hidden sm:inline transition-colors ${i === current ? 'text-blue-400 font-medium' : 'text-gray-500 group-hover:text-gray-400'}`}>
                  {s.title}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Auto-play indicator */}
        {isAutoPlaying && (
          <div className="mt-3 flex items-center justify-center">
            <div className="h-0.5 w-32 bg-gray-800 rounded-full overflow-hidden">
              <div
                key={current}
                className="h-full bg-blue-500 rounded-full"
                style={{ animation: 'progressBar 5s linear' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Features Grid */}
      <div className="max-w-5xl mx-auto px-4 pb-14">
        <h2 className="text-xl font-bold text-center mb-8 text-gray-200">Everything You Need</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {features.map((f, i) => (
            <div
              key={i}
              data-testid={`feature-card-${i}`}
              className="bg-gray-900/40 border border-gray-700/30 rounded-xl p-4 text-center hover:border-blue-500/40 transition-all"
            >
              <f.icon className="mx-auto text-blue-400 mb-2" size={24} />
              <h3 className="font-semibold text-sm mb-1 text-gray-200">{f.title}</h3>
              <p className="text-gray-500 text-xs leading-snug">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-2xl mx-auto px-4 pb-16 text-center">
        <div className="bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border border-blue-700/25 rounded-2xl p-8">
          <h2 className="text-xl font-bold mb-2">Stop Juggling Spreadsheets</h2>
          <p className="text-gray-400 text-sm mb-5">Start enjoying the game.</p>
          <button
            onClick={() => navigate('/?register=true')}
            className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-blue-600/20"
          >
            Get Started
          </button>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxIdx !== null && (
        <div
          data-testid="lightbox-modal"
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={() => setLightboxIdx(null)}
          style={{ animation: 'fadeIn 0.2s ease-out' }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') lightboxNext();
            else if (e.key === 'ArrowLeft') lightboxPrev();
            else if (e.key === 'Escape') setLightboxIdx(null);
          }}
          tabIndex={0}
          ref={(el) => el && el.focus()}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />

          {/* Left arrow */}
          <button
            data-testid="lightbox-prev-btn"
            onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/60 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-colors"
          >
            <ChevronLeft size={24} className="text-white" />
          </button>

          {/* Right arrow */}
          <button
            data-testid="lightbox-next-btn"
            onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/60 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-colors"
          >
            <ChevronRight size={24} className="text-white" />
          </button>

          {/* Image */}
          <div className="relative max-w-[90vw] max-h-[92vh] mx-16" onClick={(e) => e.stopPropagation()}>
            <img
              key={lightboxIdx}
              src={allImages[lightboxIdx].src}
              alt="Expanded view"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              style={{ animation: 'zoomIn 0.25s ease-out' }}
            />
            {/* Close button */}
            <button
              data-testid="lightbox-close-btn"
              onClick={() => setLightboxIdx(null)}
              className="absolute -top-3 -right-3 w-10 h-10 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-full flex items-center justify-center transition-colors shadow-lg"
            >
              <X size={20} className="text-white" />
            </button>
            {/* Caption + counter */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg flex items-end justify-between">
              <div>
                <p className="text-white text-sm font-medium">{allImages[lightboxIdx].title}</p>
                <p className="text-gray-300 text-xs mt-0.5">{allImages[lightboxIdx].desc}</p>
              </div>
              <span className="text-gray-400 text-xs whitespace-nowrap ml-4">{lightboxIdx + 1} / {allImages.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* CSS animations */}
      <style>{`
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateX(${direction > 0 ? '20px' : '-20px'}); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes progressBar {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes zoomIn {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
